import { useApolloClient, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { EmailArrayField } from "components/Forms/EmailArrayField";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  GetWasteShipmentQuery,
  Issue,
  IssueLevel,
  ManifestRequestFormat,
  ShippableContainerItemFragment,
  UnitType,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Control, Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import { formatQuantity, isLocalDateInFuture, UNIT_LABELS } from "../Containers/containerDisplay";
import {
  CANCEL_WASTE_SHIPMENT_MUTATION,
  DELETE_WASTE_SHIPMENT_MUTATION,
  EMAIL_MANIFEST_REQUEST_MUTATION,
  GENERATE_MANIFEST_REQUEST_MUTATION,
  GET_WASTE_SHIPMENT_QUERY,
  MARK_SHIPMENT_REQUESTED_MUTATION,
  MARK_SHIPMENT_SHIPPED_MUTATION,
  SAVE_WASTE_SHIPMENT_MUTATION,
  SHIPMENT_REFETCH_QUERIES,
  SHIPPABLE_CONTAINERS_QUERY,
  VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT,
  VALIDATE_UPDATE_SHIPMENT_LINES_INPUT,
  VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { isShipmentLevelDiff } from "./reconciliationDisplay";
import { ShipmentContentsCard } from "./ShipmentContentsCard";
import {
  ShipmentAlerts,
  ShipmentDetailSkeleton,
  ShipmentInformationCard,
  ShipmentPageGrid,
  ShipmentPageShell,
  ShipmentRail,
} from "./ShipmentDetailChrome";
import {
  EditShipmentFormValues,
  ShipmentDetailsFacts,
  ShipmentDetailsFields,
} from "./ShipmentDetailsCard";
import { formatTotalsByUnit, SAVE_BEFORE_ACTING } from "./shipmentDisplay";
import { ShipmentManifestSection } from "./ShipmentManifestSection";
import { ShipmentPageHeader } from "./ShipmentPageHeader";
import { shipmentStepActions } from "./shipmentStepActions";
import { groupContainersByStream } from "./shippableContainerGrouping";
import { ShippableContainerGroup, ShippableContainerHeader } from "./ShippableContainerRow";
import { useContainerSelection } from "./useContainerSelection";
import { DisplayLine, useStagedShipmentLines } from "./useStagedShipmentLines";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

const REFETCH = SHIPMENT_REFETCH_QUERIES;

/**
 * Why the contents cannot be edited, or undefined when they can.
 *
 * An attached manifest freezes them: the manifest is the regulated record of
 * what left the site and its lines are matched to these containers, so editing
 * them afterwards would silently invalidate those links. The server enforces
 * it; this is what lets the UI say so before a save is rejected.
 */
function contentLockReasonFor(shipment: Shipment): string | undefined {
  return shipment.manifestReceivedAt != null
    ? "Detach the manifest to change this shipment's contents."
    : undefined;
}

const isEditable = (status: WasteShipmentStatus) =>
  status === WasteShipmentStatus.Draft || status === WasteShipmentStatus.Requested;

/**
 * The rare and irreversible actions, kept out of the header's main row so the
 * one contained button is unambiguously the next step.
 *
 * Only those. The request document used to live here too, which put "open the
 * PDF we sent the vendor" one item above "delete this shipment" — two things
 * with nothing in common but being hard to place. It now sits under the
 * Request pickup step in the rail, beside the milestone that produced it, and
 * this menu is left holding exactly what its name claims.
 *
 * Not the shared `ActionsMenu`: that renders `size="large"` and collapses to a
 * plain button when it holds a single item, so it would change size and shape
 * as the shipment moves through its statuses. This stays an icon at the page's
 * small control size.
 */
function ShipmentOverflowMenu({
  shipment,
  onAbandon,
}: {
  shipment: Shipment;
  onAbandon: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const items: { key: string; label: string; onClick: () => void; disabled?: boolean }[] = [];
  if (isEditable(shipment.status)) {
    items.push({
      key: "abandon",
      label: isDraft ? "Delete shipment" : "Cancel shipment",
      onClick: run(onAbandon),
    });
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label="More actions"
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {items.map((item) => (
          <MenuItem key={item.key} onClick={item.onClick} disabled={item.disabled}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The page's commit point, in the header card rather than a bar floating over
 * the bottom of the viewport.
 *
 * There is no edit mode to enter. An editable shipment is simply editable: the
 * fields are fields and the container rows carry their own actions, so the page
 * never changes shape underneath the reader. That leaves nothing for these
 * controls to do until something has actually been changed, which is why they
 * render only once there is a change to commit — an idle page has no Save to
 * disable and no count of nothing to report.
 *
 * The header fields and the container changes share one Save, because the
 * shipment commits as a single unit. Discard drops the staged edits rather than
 * navigating: here the form IS the page, so going back would take the user off
 * the shipment entirely.
 */
function ShipmentEditActions({
  unsavedCount,
  issues,
  saving,
  onSave,
  onDiscard,
}: {
  unsavedCount: number;
  issues: Issue[];
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  if (unsavedCount === 0) {
    return null;
  }
  const blocked = hasCriticalIssues(issues);

  return (
    <>
      {/* The only thing that counts a staged container removal: the rows show
          themselves struck through, but nothing else totals them. */}
      <Typography variant="caption" color="text.secondary">
        {unsavedCount} unsaved {pluralize("change", unsavedCount)}
      </Typography>
      <IssueListButton issues={issues} />
      <Button size="small" onClick={onDiscard}>
        Discard
      </Button>
      <Tooltip title={blocked ? "Resolve the issues above first" : ""}>
        <span>
          <Button
            size="small"
            variant="contained"
            loading={saving}
            disabled={blocked}
            onClick={onSave}
          >
            Save changes
          </Button>
        </span>
      </Tooltip>
    </>
  );
}

export function WasteShipmentDetail() {
  const { tenantId } = useTenant();
  const { shipmentId } = useParams<{ shipmentId: string }>();

  const { data, loading } = useQuery(GET_WASTE_SHIPMENT_QUERY, {
    variables: { id: shipmentId ?? "" },
    skip: !shipmentId,
  });
  const shipment = data?.wasteShipment ?? null;

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    // No placeholder crumb: a generic "Shipment" that swaps to the real number
    // a moment later reads as the page changing its mind. Better to have the
    // trail arrive one step short and then complete.
    ...(shipment ? [{ label: shipment.manifestRequestNumber }] : []),
  ]);

  if (loading && !shipment) {
    return <ShipmentDetailSkeleton />;
  }

  if (!shipment) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Shipment not found</Typography>
      </Box>
    );
  }

  // Keyed on the shipment so a different one starts with clean staged state.
  return <LoadedShipmentDetail key={shipment.id} shipment={shipment} />;
}

/**
 * The shipment page edits as one unit: the header fields and the container
 * changes share a single Save and Discard. Removing a container and thinking
 * better of it should cost nothing, and a shipment should never be
 * half-edited — particularly once its request is in a vendor's hands.
 */
function LoadedShipmentDetail({ shipment }: { shipment: Shipment }) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const alerts = useAlerts();

  const [addOpen, setAddOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [editLine, setEditLine] = useState<DisplayLine | null>(null);
  /** Set while deleting, so the leave-guard doesn't argue about a doomed draft. */
  const [abandoning, setAbandoning] = useState(false);

  const staged = useStagedShipmentLines(shipment);
  const form = useShipmentHeaderForm(shipment);
  const {
    formState: { isDirty: headerDirty, dirtyFields },
    getValues,
    reset,
    issues: headerIssues,
  } = form;

  const [save, { loading: saving }] = useMutation(SAVE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [cancelShipment, { loading: canceling }] = useMutation(CANCEL_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [deleteShipment, { loading: deleting }] = useMutation(DELETE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });

  // Split the comparison by grain. The discriminator for a shipment-level row
  // is the field, NOT a null `wasteStreamId` -- null is also the identity of
  // the uncharacterized bucket, so waste with no stream assigned produces real
  // quantity and code rows that happen to carry a null id.
  /*
    One memo over the whole sort, because `diffsByStream` is handed to the
    contents grid and its identity is what the grid's column memo keys on.
    Rebuilt every render, it made that memo — and the grouping column definition
    that depends on it — recompute on every keystroke in the Details fields, for
    exactly the manifest-attached shipments where the grid is heaviest and also
    carries the Discrepancies column.
  */
  const { diffsByStream, manifestOnlyDiffs, shipmentLevelDiffs, uncharacterizedDiffs } =
    useMemo(() => {
      const streamIds = new Set(
        shipment.lines.map((line) => line.wasteStream?.id).filter((id): id is string => !!id)
      );
      const hasUncharacterizedLines = shipment.lines.some((line) => !line.wasteStream);
      const byStream = new Map<string, typeof shipment.reconciliation.fieldDiffs>();
      const manifestOnly: typeof shipment.reconciliation.fieldDiffs = [];
      const shipmentLevel: typeof shipment.reconciliation.fieldDiffs = [];
      const uncharacterized: typeof shipment.reconciliation.fieldDiffs = [];
      for (const diff of shipment.reconciliation.fieldDiffs) {
        if (isShipmentLevelDiff(diff.field)) {
          shipmentLevel.push(diff);
        } else if (!diff.wasteStreamId) {
          // Uncharacterized waste on one side or both. It belongs with the
          // contents when this shipment actually carried some, and reads as
          // manifest-only when it did not.
          if (hasUncharacterizedLines) {
            uncharacterized.push(diff);
          } else {
            manifestOnly.push(diff);
          }
        } else if (streamIds.has(diff.wasteStreamId)) {
          byStream.set(diff.wasteStreamId, [...(byStream.get(diff.wasteStreamId) ?? []), diff]);
        } else {
          manifestOnly.push(diff);
        }
      }
      return {
        diffsByStream: byStream,
        manifestOnlyDiffs: manifestOnly,
        shipmentLevelDiffs: shipmentLevel,
        uncharacterizedDiffs: uncharacterized,
      };
    }, [shipment.lines, shipment.reconciliation.fieldDiffs]);

  /*
    Whether a request document was ever produced, asked of `requestGeneratedAt`
    rather than `requestExportUrl`. The URL is an S3 key signed on every read and
    comes back null whenever signing fails, so a document that does exist would
    read as none — the wrong-facts case the stale-request alert exists to prevent.
    The timestamp is written beside the key and cannot fail.

    Every surface that describes what happened reads this one value: the alert,
    and the rail's request button. The link itself is still what decides whether
    View is offered, since that is a question about right now. Splitting the two
    is what stops a signing failure making the page contradict itself.
  */
  const requestDocumentExists = shipment.requestGeneratedAt != null;

  // Editable or not, with no mode in between. A shipment the vendor has not
  // taken yet is one the user can change, so its fields are fields; once it has
  // shipped or been canceled the record is closed and they are facts. Toggling
  // between the two on a shipment that was editable all along only made the
  // page change shape.
  const canEdit = isEditable(shipment.status);
  const unsavedCount = Object.keys(dirtyFields).length + staged.changeCount;
  const hasUnsaved = unsavedCount > 0;

  // An attached manifest freezes the contents. The manifest is the regulated
  // record of what left the site, and its lines are matched to these containers
  // — editing them afterwards would silently invalidate those links. The server
  // enforces this; saying so here means the user learns it from the disabled
  // control rather than from a rejected save.
  const contentLockReason = contentLockReasonFor(shipment);

  const handleSave = async () => {
    if (hasCriticalIssues(headerIssues)) {
      return;
    }
    const values = getValues();
    const linesInput = staged.toUpdateLinesInput();
    try {
      // One mutation, one transaction. Sending the header, container and pickup
      // changes separately meant a rule that rejected the second could leave the
      // first committed while the user was told the save failed.
      //
      // Only the sections the user touched are included — presence is what tells
      // the server which parts of the form to apply. In particular the pickup
      // section is sent only when its field is dirty, because a null date there
      // is a real instruction (clear the pickup and release its containers)
      // rather than "unchanged".
      await save({
        variables: {
          input: {
            id: shipment.id,
            ...(headerDirty
              ? {
                  header: toShipmentHeaderFields(values, Boolean(dirtyFields.plannedShipDate)),
                }
              : {}),
            ...(linesInput
              ? {
                  lines: {
                    addContainerIds: linesInput.addContainerIds,
                    removeLineIds: linesInput.removeLineIds,
                    setQuantities: linesInput.setQuantities,
                  },
                }
              : {}),
            ...(dirtyFields.scheduledPickupDate
              ? {
                  pickup: {
                    scheduledPickupDate: values.scheduledPickupDate
                      ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.scheduledPickupDate))
                      : null,
                  },
                }
              : {}),
          },
        },
      });
      staged.reset();
      // The header form keeps dirty values across a reset so the refetch can't
      // clobber someone mid-edit — which also means a successful save leaves it
      // looking unsaved. Rebase it on what was just persisted, or the page goes
      // on claiming changes and "Mark shipped" stays disabled until the user
      // discards an edit that already landed.
      reset(values);
      alerts.success("Shipment updated.");
    } catch (e) {
      // Nothing to unwind on the client: the save is one transaction, so a
      // failure means nothing persisted and every edit is still pending. The
      // form keeps its dirty state, which is now the truth rather than a guess.
      alerts.error(e instanceof Error ? e.message : "Failed to update the shipment.");
    }
  };

  // Throws away whatever is staged and leaves the shipment as it was saved.
  const handleDiscard = () => {
    reset();
    staged.reset();
  };

  // A draft never left the building, so it is deleted rather than left behind
  // as a canceled row; once requested, the vendor has its number and the
  // record stays.
  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const handleAbandon = async () => {
    try {
      if (isDraft) {
        // Deleting the shipment navigates away, and the staged edits go with
        // it on purpose — the guard must not stop and ask about changes to a
        // record the user just chose to destroy.
        setAbandoning(true);
        await deleteShipment({ variables: { id: shipment.id } });
        alerts.success(`Shipment ${shipment.manifestRequestNumber} deleted.`);
        setCancelOpen(false);
        navigate(`/o/${tenantId}/waste/shipments`);
        return;
      }
      await cancelShipment({ variables: { id: shipment.id } });
      alerts.success(`Shipment ${shipment.manifestRequestNumber} canceled.`);
      // Canceling makes the shipment read-only, so the fields that produced any
      // pending edits unmount — but their dirty state would outlive them and
      // leave the guard asking about changes the user can no longer see, save,
      // or discard.
      handleDiscard();
      setCancelOpen(false);
    } catch (e) {
      // The user is still here, and so are their staged edits, so the guard has
      // to come back — a delete that failed must not be the reason the next
      // navigation walks away from them without asking.
      setAbandoning(false);
      alerts.error(
        e instanceof Error ? e.message : `Failed to ${isDraft ? "delete" : "cancel"} the shipment.`
      );
    }
  };

  return (
    <ShipmentPageShell
      header={
        <ShipmentPageHeader
          shipment={shipment}
          actions={
            <>
              {canEdit && (
                <ShipmentEditActions
                  unsavedCount={unsavedCount}
                  issues={headerIssues}
                  saving={saving}
                  onSave={handleSave}
                  onDiscard={handleDiscard}
                />
              )}
              <ShipmentOverflowMenu shipment={shipment} onAbandon={() => setCancelOpen(true)} />
            </>
          }
        />
      }
    >
      {/*
        The page has no edit mode to leave, and its Save sits at the top of a
        long page — so an edit can scroll out of sight and be walked away from
        without a trace. Staged container removals are the ones that would hurt:
        they live only in this component's state.
      */}
      <UnfinishedChangesPrompt when={hasUnsaved && !abandoning} />

      <ShipmentAlerts
        issues={shipment.issues}
        requestStale={canEdit && shipment.manifestRequestStale}
        requestDocumentExists={requestDocumentExists}
        resendBlockedReason={hasUnsaved ? SAVE_BEFORE_ACTING : undefined}
        onResendRequest={canEdit ? () => setRequestOpen(true) : undefined}
      />

      <ShipmentPageGrid
        body={
          <>
            <ShipmentInformationCard shipment={shipment} />

            {/* Full width now that the manifest has moved into the rail: six
                fields sharing a row with a card had ~420px to work with. */}
            {canEdit ? (
              <ShipmentDetailsFields form={form} />
            ) : (
              <ShipmentDetailsFacts shipment={shipment} />
            )}
          </>
        }
        rail={
          <ShipmentRail
            shipment={shipment}
            stepActions={{
              ...shipmentStepActions({
                canEdit,
                isDraft,
                isRequested: shipment.status === WasteShipmentStatus.Requested,
                hasUnsaved,
                requestExportUrl: shipment.requestExportUrl,
                requestDocumentExists,
                onRequestPickup: () => setRequestOpen(true),
                onMarkShipped: () => setShipOpen(true),
                onViewRequest: () =>
                  shipment.requestExportUrl && openInNewTab(shipment.requestExportUrl),
              }),
              manifest: (
                <ShipmentManifestSection
                  shipment={shipment}
                  // Staged CONTAINER changes only, not header edits: attaching
                  // freezes the contents, so a pending add or removal would
                  // become unsaveable the moment a manifest lands. Header edits
                  // are unaffected by the lock, so they must not block it.
                  attachBlockedReason={
                    staged.changeCount > 0
                      ? "Save or discard your container changes first — attaching a manifest freezes this shipment's contents."
                      : undefined
                  }
                />
              ),
            }}
          />
        }
      />

      {/* Full width, below the rail: the contents are the widest thing on the
          page and the only part that is a table. */}
      <ShipmentContentsCard
        shipment={shipment}
        lines={staged.displayLines}
        editable={canEdit}
        contentLockReason={contentLockReason}
        issues={shipment.issues}
        onAddContainers={() => setAddOpen(true)}
        onEditLine={setEditLine}
        onRemoveLine={staged.removeLine}
        onRestoreLine={staged.restoreLine}
        diffsByStream={diffsByStream}
        manifestOnlyDiffs={manifestOnlyDiffs}
        shipmentLevelDiffs={shipmentLevelDiffs}
        uncharacterizedDiffs={uncharacterizedDiffs}
      />

      {addOpen && (
        <AddContainersDialog
          shipment={shipment}
          stagedContainerIds={staged.stagedContainerIds}
          onStage={staged.addContainers}
          onClose={() => setAddOpen(false)}
        />
      )}
      {shipOpen && (
        <MarkShipmentShippedDialog shipment={shipment} onClose={() => setShipOpen(false)} />
      )}
      {requestOpen && (
        <RequestPickupDialog shipment={shipment} onClose={() => setRequestOpen(false)} />
      )}
      {editLine?.lineId && (
        <EditLineQuantityDialog
          shipmentId={shipment.id}
          line={{ ...editLine, lineId: editLine.lineId }}
          onStage={staged.setQuantity}
          onClose={() => setEditLine(null)}
        />
      )}
      {cancelOpen && (
        <Dialog
          open
          onClose={() => setCancelOpen(false)}
          maxWidth="xs"
          title={isDraft ? "Delete this shipment?" : "Cancel this shipment?"}
          primaryActionButtonProps={{
            saveText: isDraft ? "Delete shipment" : "Cancel shipment",
            loading: canceling || deleting,
            onClick: handleAbandon,
          }}
        >
          <AbandonShipmentExplanation shipment={shipment} isDraft={isDraft} />
        </Dialog>
      )}
    </ShipmentPageShell>
  );
}

/**
 * What abandoning this shipment will actually do.
 *
 * Three cases rather than two, and its own component because they cannot be a
 * ternary chain. The third is the one that was missing: canceling releases an
 * attached manifest — the link, the received date, the signed flag and every
 * line's match to a manifest line — and nothing said so. Losing the tie to a
 * signed regulated document on an unlabelled confirm is not a surprise anyone
 * should get, least of all on an action with no undo.
 */
function AbandonShipmentExplanation({
  shipment,
  isDraft,
}: {
  shipment: Shipment;
  isDraft: boolean;
}) {
  if (isDraft) {
    return (
      <Typography variant="body2">
        {shipment.manifestRequestNumber} has not gone to a vendor, so it will be deleted outright.
        Its containers are untouched and stay available to package.
      </Typography>
    );
  }

  // The shipment's own snapshot first: it survives the manifest row being
  // rebuilt by the pipeline, so it is what can always be named.
  const trackingNumber =
    shipment.reconciliation.linkedManifestTrackingNumber ??
    shipment.manifest?.manifestTrackingNumber ??
    null;
  const manifestAttached = shipment.manifestReceivedAt != null;

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        {shipment.manifestRequestNumber} will be marked canceled and kept, because the vendor
        already has its request. No container amounts change — removals only happen when a shipment
        ships.
      </Typography>
      {manifestAttached && (
        <Alert severity="warning">
          {trackingNumber
            ? `Manifest ${trackingNumber} will be detached, along with the waste lines matched to this shipment's containers.`
            : "The attached manifest will be detached, along with the waste lines matched to this shipment's containers."}{" "}
          It stays in Encamp and can be attached to whichever shipment really carried the waste.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        Canceling cannot be undone.
      </Typography>
    </Stack>
  );
}

/**
 * PDF leads: it is the one a vendor can print and file as-is, and the
 * spreadsheets exist for anyone who wants to work the numbers.
 */
const REQUEST_FORMAT_OPTIONS: { value: ManifestRequestFormat; label: string }[] = [
  { value: ManifestRequestFormat.Pdf, label: "PDF" },
  { value: ManifestRequestFormat.Csv, label: "CSV" },
  { value: ManifestRequestFormat.Xlsx, label: "Excel (XLSX)" },
];

/** Open a signed URL without navigating away from the shipment. */
function openInNewTab(url: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.click();
}

/**
 * The vendor-facing half of the shipment: produce the manifest request, send
 * it, or record that it was arranged some other way.
 *
 * All three actions move a draft to Requested, which is why they live together
 * — the request is a single milestone reached by three routes, not three
 * separate states.
 */
/** How the vendor is told about the pickup. One decision, three routes. */
type RequestRoute = "email" | "download" | "elsewhere";

/** The confirm button names the route's outcome, so it is keyed off the route. */
const PRIMARY_ACTION_LABEL: Record<RequestRoute, string> = {
  email: "Send request",
  download: "Download request",
  elsewhere: "Mark as requested",
};

type RequestPickupFormValues = {
  route: RequestRoute;
  format: ManifestRequestFormat;
  recipients: string[];
};

/** Rows in the vendor preview are one stream per unit, keyed the same way. */
function streamRowKey(line: Shipment["manifestRequestLines"][number]): string {
  return line.wasteStream?.id ?? "uncharacterized";
}

/**
 * The format choice belongs to the route that produces a document, so it
 * renders indented under whichever of those routes is selected rather than as
 * a dialog-wide setting.
 */
function RequestFormatField({ control }: { control: Control<RequestPickupFormValues> }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Controller
        control={control}
        name="format"
        render={({ field }) => (
          <TextField {...field} select size="small" label="Format" sx={{ width: 180 }}>
            {REQUEST_FORMAT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Typography variant="caption" color="text.secondary">
        PDF to print or forward · CSV and Excel to work the numbers
      </Typography>
    </Stack>
  );
}

/**
 * One dialog for the whole "tell the vendor" step.
 *
 * Generating a document, emailing it, and recording a request made by phone are
 * not three separate actions — they are three answers to "how do you want to
 * send it", so they are one radio rather than three buttons competing on the
 * page. The shipment lands in REQUESTED either way.
 *
 * It leads with what the vendor will actually receive: before this, the only
 * way to know was to generate a file and open it.
 */
function RequestPickupDialog({ shipment, onClose }: { shipment: Shipment; onClose: () => void }) {
  const alerts = useAlerts();
  const client = useApolloClient();

  const form = useForm<RequestPickupFormValues>({
    defaultValues: {
      route: "email",
      format: ManifestRequestFormat.Pdf,
      recipients: [],
    },
  });
  const { control, getValues, watch, setError } = form;
  const route = watch("route");
  // Re-sending from REQUESTED costs nothing; it is the first move off a draft
  // that gives up the ability to delete.
  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const [generate, { loading: generating }] = useMutation(GENERATE_MANIFEST_REQUEST_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [emailRequest, { loading: emailing }] = useMutation(EMAIL_MANIFEST_REQUEST_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [markRequested, { loading: marking }] = useMutation(MARK_SHIPMENT_REQUESTED_MUTATION, {
    refetchQueries: REFETCH,
  });
  const loading = generating || emailing || marking;

  const primaryLabel = PRIMARY_ACTION_LABEL[route];

  // Lines are grouped by stream, unit, and snapshot signature. A stream
  // stored in two units renders as two rows; label those with the unit so
  // they don't read as duplicates. Signature-only splits keep the bare name —
  // the unit is not what distinguishes them.
  const streamUnits = new Map<string, Set<string>>();
  for (const line of shipment.manifestRequestLines) {
    const key = streamRowKey(line);
    const units = streamUnits.get(key) ?? new Set<string>();
    units.add(line.unit);
    streamUnits.set(key, units);
  }

  const handleConfirm = async () => {
    if (loading) {
      return;
    }
    const values = getValues();
    // Validated on submit rather than by disabling the button: a disabled
    // button can't say why it is disabled, and it is unreachable to keyboard
    // and screen-reader users.
    if (values.route === "email" && values.recipients.length === 0) {
      setError("recipients", {
        type: "required",
        message: "Add at least one recipient to send the request.",
      });
      return;
    }
    try {
      if (values.route === "email") {
        await emailRequest({
          variables: {
            shipmentId: shipment.id,
            format: values.format,
            recipients: values.recipients,
          },
        });
        alerts.success(
          `Manifest request ${shipment.manifestRequestNumber} sent to ${values.recipients.length} ${
            values.recipients.length === 1 ? "recipient" : "recipients"
          }.`
        );
      } else if (values.route === "download") {
        const { data } = await generate({
          variables: { shipmentId: shipment.id, format: values.format },
        });
        const url = data?.generateManifestRequest.url;
        if (url) {
          openInNewTab(url);
        }
        alerts.success(`Manifest request generated for ${shipment.manifestRequestNumber}.`);
      } else {
        await markRequested({ variables: { shipmentId: shipment.id } });
        alerts.success(`${shipment.manifestRequestNumber} is marked requested.`);
      }
      onClose();
    } catch (e) {
      // Emailing is not all-or-nothing: the shipment flips to REQUESTED on the
      // FIRST recipient that succeeds, so a failure on a later one leaves the
      // server ahead of this page. Apollo runs refetchQueries only on success,
      // so without this the user is looking at DRAFT for a shipment that is
      // already requested, and only a reload would tell them otherwise.
      await client.refetchQueries({ include: REFETCH }).catch(() => {
        // The error below is the useful one; a failed re-sync must not replace it.
      });
      alerts.error(e instanceof Error ? e.message : "Failed to request the pickup.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Request pickup for ${shipment.manifestRequestNumber}`}
      primaryActionButtonProps={{
        saveText: primaryLabel,
        loading,
        disabled: loading,
        onClick: handleConfirm,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* The reassurance that used to sit in an alert at the bottom, said
              once here instead: by the time a reader has reached the send
              button they have already committed, and each route states its own
              consequence anyway. */}
          <Typography variant="body2" color="text.secondary">
            Your vendor gets this shipment summarized by waste stream. It is not an official
            manifest, so they create that and send it back. You can still edit this shipment
            afterwards.
          </Typography>

          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                What the vendor sees
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formatTotalsByUnit(shipment.totalQuantityByUnit)}
              </Typography>
            </Stack>
            {shipment.manifestRequestLines.map((line, i) => (
              <Stack
                key={`${line.wasteStream?.id ?? "uncharacterized"}-${line.unit}-${i}`}
                direction="row"
                spacing={1}
                alignItems="baseline"
                sx={{ py: 0.25 }}
              >
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {line.wasteStream?.name ?? "Uncharacterized"}
                  {(streamUnits.get(streamRowKey(line))?.size ?? 0) > 1 &&
                    ` (${UNIT_LABELS[line.unit].toLowerCase()})`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {line.containerCount} {line.containerCount === 1 ? "container" : "containers"}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatQuantity(line.quantity, line.unit)}
                </Typography>
              </Stack>
            ))}
            <Typography variant="caption" color="text.secondary">
              Plus a per-container appendix for your records.
            </Typography>
          </Paper>

          <Controller
            control={control}
            name="route"
            render={({ field }) => (
              <FormControl>
                <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>How do you want to send it?</FormLabel>
                {/* Said once, above the three routes, because all three make the
                    same one-way move — and only from a draft, which is the only
                    status where anything is lost. A draft can be deleted and a
                    requested shipment can only be canceled, so this is the click
                    that spends that, and none of the three options said so. */}
                {isDraft && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    Any of these marks the shipment requested. After that it can be canceled but no
                    longer deleted.
                  </Typography>
                )}
                <RadioGroup {...field} sx={{ gap: 0.75 }}>
                  <FormControlLabel
                    value="email"
                    sx={{ alignItems: "flex-start" }}
                    control={<Radio size="small" sx={{ pt: 0.25 }} />}
                    label={
                      <Box>
                        <Typography variant="body2">Email it to the vendor now</Typography>
                        <Typography variant="caption" color="text.secondary">
                          They get the file attached, so nothing expires.
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="download"
                    sx={{ alignItems: "flex-start" }}
                    control={<Radio size="small" sx={{ pt: 0.25 }} />}
                    label={
                      <Box>
                        <Typography variant="body2">Download it and send it myself</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Marks the shipment requested once the file is created.
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="elsewhere"
                    sx={{ alignItems: "flex-start" }}
                    control={<Radio size="small" sx={{ pt: 0.25 }} />}
                    label={
                      <Box>
                        <Typography variant="body2">I already requested it another way</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Phoned in, or entered in the vendor&apos;s own portal. No document is
                          created.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          {/* Below the three options rather than indented under the selected
              one. Inline, the email route's recipients and format pushed the
              other two routes off the bottom of the dialog, so the question
              arrived with its answer apparently already given — and the way to
              discover there were alternatives was to scroll.
              The rule keeps them from reading as part of the last option they
              happen to sit under. Only the two routes that produce a document
              have anything to fill in. */}
          {route !== "elsewhere" && (
            <>
              <Divider />
              <Stack spacing={2}>
                {route === "email" && (
                  <EmailArrayField name="recipients" label="Vendor recipients" />
                )}
                <RequestFormatField control={control} />
              </Stack>
            </>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
function toUpdateShipmentInput(
  shipmentId: string,
  values: EditShipmentFormValues,
  plannedShipDateDirty: boolean
) {
  return { id: shipmentId, ...toShipmentHeaderFields(values, plannedShipDateDirty) };
}

/**
 * The header fields alone, shared by the validator query (which wants them
 * under an id) and the combined Save's `header` section (which does not), so
 * the two can never normalize a value differently.
 */
function toShipmentHeaderFields(values: EditShipmentFormValues, plannedShipDateDirty: boolean) {
  return {
    vendorId: values.vendorId,
    transporterName: values.transporterName.trim() || null,
    destinationName: values.destinationName.trim() || null,
    destinationEpaId: values.destinationEpaId.trim() || null,
    // Only re-normalize the date when the user actually touched it, so an
    // untouched value can't shift a day through the local timezone.
    ...(plannedShipDateDirty
      ? {
          plannedShipDate: values.plannedShipDate
            ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.plannedShipDate))
            : null,
        }
      : {}),
  };
}

/**
 * Async header-edit validation; the seam future field checks land in. The
 * validation payload always includes the date (nothing persists from it, so
 * the dirty-gated timezone guard only matters on the real mutation input).
 */
function useShipmentUpdateValidation(shipmentId: string) {
  const [query] = useLazyQuery(VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: EditShipmentFormValues): Promise<Issue[]> => {
      const { data } = await query({
        variables: { input: toUpdateShipmentInput(shipmentId, values, true) },
      });
      return data?.wasteShipmentUpdateInputValidator ?? [];
    },
    [query, shipmentId]
  );
}

/**
 * The shipment's header as a live inline form — the single place vendor,
 * transporter, TSDF, and dates are collected. Save/Discard appear only when
 * dirty; background refetches keep in-progress edits (keepDirtyValuesOnReset).
 * 3C note: once requests generate, a header edit after requestGeneratedAt
 * should surface a stale-request warning here.
 */
/** The header form, owned by the page so one Save can cover the whole thing. */
function useShipmentHeaderForm(shipment: Shipment) {
  const defaultValues = useMemo<EditShipmentFormValues>(
    () => ({
      vendorId: shipment.vendor?.id ?? null,
      transporterName: shipment.transporterName ?? "",
      destinationName: shipment.destinationName ?? "",
      destinationEpaId: shipment.destinationEpaId ?? "",
      plannedShipDate: storedDateToDateOnly(shipment.plannedShipDate),
      scheduledPickupDate: storedDateToDateOnly(shipment.scheduledPickupDate),
    }),
    [shipment]
  );

  return useValidatingForm<EditShipmentFormValues>(
    defaultValues,
    [],
    useShipmentUpdateValidation(shipment.id),
    [],
    { keepDirtyValuesOnReset: true }
  );
}

function AddContainersDialog({
  shipment,
  stagedContainerIds,
  onStage,
  onClose,
}: {
  shipment: Shipment;
  /** Containers already on the shipment or already staged. */
  stagedContainerIds: string[];
  onStage: (containers: ShippableContainerItemFragment[]) => void;
  onClose: () => void;
}) {
  const { selectedIds, toggle, setSelection } = useContainerSelection();

  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: shipment.facilityId },
    fetchPolicy: "cache-and-network",
  });
  const onShipment = useMemo(() => new Set(stagedContainerIds), [stagedContainerIds]);
  const candidates = useMemo(
    () => (data?.facility?.shippableContainers?.items ?? []).filter((c) => !onShipment.has(c.id)),
    [data?.facility?.shippableContainers?.items, onShipment]
  );

  // Live warn-level validation as the selection changes — e.g. a container
  // already on another open shipment warns here just like in the builder.
  const [validate, { data: validationData }] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  useEffect(() => {
    if (selectedIds.size > 0) {
      validate({
        variables: { input: { shipmentId: shipment.id, addContainerIds: [...selectedIds] } },
      });
    }
  }, [selectedIds, shipment.id, validate]);
  const issues: Issue[] =
    selectedIds.size > 0 ? (validationData?.updateShipmentLinesInputValidator ?? []) : [];

  // Grouped by waste stream, the way the wizard and the contents grid both show
  // containers — the stream is what the vendor manifests, so it is what the
  // reader is deciding about.
  const groups = useMemo(() => groupContainersByStream(candidates), [candidates]);

  // Staged, not saved: the page has one Save, and adding a container should be
  // as undoable as any other edit on it.
  const handleAdd = () => {
    onStage(candidates.filter((c) => selectedIds.has(c.id)));
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      // Wider than the usual dialog: this now shows the same columns the
      // packaging wizard does, and at "sm" the location and deadline fell off
      // the right edge.
      maxWidth="md"
      fullWidth
      title="Add containers"
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Add",
        disabled: selectedIds.size === 0 || hasCriticalIssues(issues),
        onClick: handleAdd,
      }}
    >
      {loading && candidates.length === 0 && <Skeleton height={120} />}
      {!loading && candidates.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Every shippable container at this facility is already on the shipment.
        </Typography>
      )}
      <Stack sx={{ maxHeight: 360, overflowY: "auto" }}>
        {/*
          Sticky inside the scroller, not placed above it. The labels have to
          survive scrolling either way — the columns they name carry the meaning
          of every row and the stream headings between them don't repeat it. But
          sitting outside the scroller they kept their full width while the rows
          lost some to the scrollbar, so on any platform drawing a classic one
          the columns stopped lining up with the values beneath them. The wizard
          can place its header outside because its list has no inner scroller.
        */}
        {candidates.length > 0 && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: "background.paper",
            }}
          >
            <ShippableContainerHeader />
          </Box>
        )}
        {groups.map((group) => (
          <ShippableContainerGroup
            key={group.streamId || "uncharacterized"}
            group={group}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleGroup={setSelection}
          />
        ))}
      </Stack>
      {issues.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {issues.map((issue, i) => (
            <Alert
              key={`${issue.key}-${i}`}
              severity={issue.level === "CRITICAL" ? "error" : "warning"}
            >
              {issue.message}
            </Alert>
          ))}
        </Stack>
      )}
    </Dialog>
  );
}

/**
 * The validator's input types the quantity as a required Float, so a value that
 * isn't a number can't be sent at all — it serializes to null and the query
 * errors, which would otherwise surface as "no issues" and leave Save enabled.
 */
const UNPARSEABLE_QUANTITY_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "lineQuantityInvalid",
  message: "Enter the quantity as a number.",
  modelName: "WasteShipmentLine",
  dataName: null,
  jurisdictions: [],
};

type EditLineQuantityFormValues = {
  quantity: string;
  unit: UnitType;
};

function EditLineQuantityDialog({
  shipmentId,
  line,
  onStage,
  onClose,
}: {
  shipmentId: string;
  line: DisplayLine & { lineId: string };
  onStage: (lineId: string, quantity: number, unit: UnitType) => void;
  onClose: () => void;
}) {
  const defaultValues = useMemo<EditLineQuantityFormValues>(
    () => ({ quantity: String(line.quantity), unit: line.unit }),
    [line]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: EditLineQuantityFormValues): Promise<Issue[]> => {
      const quantity = safeParseFloat(values.quantity);
      if (quantity === null) {
        return [UNPARSEABLE_QUANTITY_ISSUE];
      }
      const { data } = await validateQuery({
        variables: {
          input: {
            shipmentId,
            setQuantities: [{ lineId: line.lineId, quantity, unit: values.unit }],
          },
        },
      });
      return data?.updateShipmentLinesInputValidator ?? [];
    },
    [validateQuery, shipmentId, line.lineId]
  );

  const form = useValidatingForm<EditLineQuantityFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    watch,
    formState: { isDirty },
    issues,
  } = form;
  const quantityValue = watch("quantity");
  const unitValue = watch("unit");

  const handleSave = () => {
    if (hasCriticalIssues(issues)) {
      return;
    }
    const values = getValues();
    const quantity = safeParseFloat(values.quantity);
    if (quantity === null) {
      return;
    }
    onStage(line.lineId, quantity, values.unit);
    onClose();
  };

  // Say it on the field, not only behind the issue button: the number the
  // reader just typed is where the objection belongs.
  const sameUnitCap =
    unitValue === line.container.amountUnit && line.container.amount != null
      ? line.container.amount
      : null;
  const typed = safeParseFloat(quantityValue);
  const overContainer = sameUnitCap != null && typed != null && typed > sameUnitCap;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={`Quantity for ${line.container.containerLabel}`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Apply",
        disabled: hasCriticalIssues(issues) || !isDirty || safeParseFloat(quantityValue) === null,
        onClick: handleSave,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Ship less than the full container (partial ship) or set the amount leaving a tank.
          </Typography>
          <FormTextField
            control={control}
            name="quantity"
            label="Quantity"
            fullWidth
            type="number"
            // Capped only when the units agree. Across units the container's
            // amount is not a bound this field can state -- that comparison
            // needs the stream's density, which the server does.
            inputProps={{
              min: 0,
              step: "any",
              ...(sameUnitCap != null ? { max: sameUnitCap } : {}),
            }}
            helperText={
              overContainer
                ? `More than the container holds (${formatQuantity(line.container.amount, line.container.amountUnit)})`
                : `Container holds ${formatQuantity(line.container.amount, line.container.amountUnit)}`
            }
            textFieldProps={{ size: "small", error: overContainer }}
          />
          <Controller
            control={control}
            name="unit"
            render={({ field }) => (
              <TextField
                label="Unit"
                size="small"
                select
                value={field.value}
                onChange={(e) => field.onChange(e.target.value as UnitType)}
              >
                {Object.values(UnitType).map((u) => (
                  <MenuItem key={u} value={u}>
                    {UNIT_LABELS[u] ?? u}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  );
}

/**
 * The ship dialog's starting date: the planned ship date when there is one and
 * it has arrived, otherwise today. A planned date still in the future would be
 * rejected as a future shipped date, so it is not offered.
 */
function plannedShippedDateDefault(plannedShipDate: string | null | undefined): string | null {
  const today = DateTime.now().startOf("day");
  const plannedDay = storedDateToDateOnly(plannedShipDate);
  if (plannedDay) {
    const planned = DateTime.fromISO(plannedDay).startOf("day");
    if (planned.isValid && planned <= today) {
      return planned.toISODate();
    }
  }
  return today.toISODate();
}

/**
 * A stored shipment date is UTC midnight standing for a calendar day, but the
 * date picker parses what it is given in the browser's zone — so handing it the
 * raw instant renders the day before for anyone behind UTC. Read the day out in
 * UTC and pass a plain date, which the picker shows verbatim.
 *
 * Done here rather than in DateField: that component is shared by 26 call
 * sites, some of which mean a real instant.
 */
function storedDateToDateOnly(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  return DateTime.fromISO(iso, { zone: "utc" }).toISODate();
}

type ShipFormValues = { shippedDate: string | null };

/**
 * Mirrors the server rule's key and message: the input serializes the picked
 * date to UTC midnight, so the server can no longer tell which calendar day the
 * user was on and keeps a looser end-of-day tolerance. Deciding it here — as the
 * container fill dialogs do — is what makes "tomorrow" wrong for a user whose
 * today is already the server's tomorrow.
 */
const SHIPPED_DATE_IN_FUTURE_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "shippedDateInFuture",
  message: "The shipped date cannot be in the future.",
  modelName: "WasteShipment",
  dataName: null,
  jurisdictions: [],
};

function toShipInput(shipmentId: string, values: ShipFormValues) {
  return {
    shipmentId,
    shippedDate: values.shippedDate
      ? (toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.shippedDate)) ?? null)
      : null,
  };
}

/**
 * The commit step, not a second editor. What is on the shipment and how much
 * of each container leaves are decisions made on the shipment page, so this
 * confirms the date and shows the one thing the page cannot: what shipping
 * will actually do to each container, and whether anything blocks it.
 */
function MarkShipmentShippedDialog({
  shipment,
  onClose,
}: {
  shipment: Shipment;
  onClose: () => void;
}) {
  const alerts = useAlerts();

  const defaultValues = useMemo<ShipFormValues>(
    () => ({
      // The planned date is what the user already told us this shipment was
      // going out on, so it beats a guess. Falls back to today when the
      // shipment was never planned, or when the plan is still in the future
      // and the waste is leaving now.
      shippedDate: plannedShippedDateDefault(shipment.plannedShipDate),
    }),
    [shipment.plannedShipDate]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: ShipFormValues): Promise<Issue[]> => {
      const input = toShipInput(shipment.id, values);
      if (!input.shippedDate) {
        return [];
      }
      if (isLocalDateInFuture(values.shippedDate)) {
        return [SHIPPED_DATE_IN_FUTURE_ISSUE];
      }
      const { data } = await validateQuery({ variables: { input } });
      return data?.markShipmentShippedInputValidator ?? [];
    },
    [validateQuery, shipment.id]
  );

  // Seeded from the shipment's own standing issues so a blocked shipment says
  // so the moment this opens, without waiting on a round-trip.
  const form = useValidatingForm<ShipFormValues>(defaultValues, shipment.issues, validateInput, []);
  const { control, getValues, watch, issues } = form;

  const [markShipped, { loading }] = useMutation(MARK_SHIPMENT_SHIPPED_MUTATION, {
    refetchQueries: REFETCH,
  });

  const shippedDate = watch("shippedDate");
  const blocked = hasCriticalIssues(issues) || !shippedDate || loading;
  const blockers = issues.filter((issue) => issue.level === IssueLevel.Critical);
  const preview = shipment.shipPreview;

  const effects = [
    preview.archivedCount > 0
      ? `archive ${preview.archivedCount} one-way ${preview.archivedCount === 1 ? "container" : "containers"}`
      : null,
    preview.emptiedTankCount > 0
      ? `empty ${preview.emptiedTankCount} ${preview.emptiedTankCount === 1 ? "tank" : "tanks"} and return ${preview.emptiedTankCount === 1 ? "it" : "them"} to service`
      : null,
  ].filter((effect): effect is string => effect !== null);

  const handleShip = async () => {
    if (blocked) {
      return;
    }
    const input = toShipInput(shipment.id, getValues());
    if (!input.shippedDate) {
      return;
    }
    try {
      await markShipped({ variables: { input } });
      alerts.success(`${shipment.manifestRequestNumber} is marked shipped.`);
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to mark the shipment shipped.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Mark ${shipment.manifestRequestNumber} shipped`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Mark shipped",
        loading,
        disabled: blocked,
        onClick: handleShip,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This records {formatTotalsByUnit(shipment.totalQuantityByUnit)} across{" "}
            {shipment.lines.length} {shipment.lines.length === 1 ? "container" : "containers"} as
            having left the site.
          </Typography>

          <Box>
            <DateField
              control={control}
              name="shippedDate"
              label="Shipped date"
              datePickerProps={{
                disableFuture: true,
                slotProps: { textField: { size: "small", sx: { width: 220 } } },
              }}
            />
          </Box>

          {blockers.length > 0 ? (
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {blockers.length} {blockers.length === 1 ? "problem" : "problems"} to fix before
                this can ship
              </Typography>
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.25}>
                {blockers.map((issue, i) => (
                  <Typography key={`${issue.key}-${i}`} component="li" variant="caption">
                    {issue.message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : (
            (effects.length > 0 || preview.keepsBalance.length > 0) && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Shipping this will
                </Typography>
                <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.25}>
                  {effects.map((effect) => (
                    <Typography key={effect} component="li" variant="body2" color="text.secondary">
                      {effect}
                    </Typography>
                  ))}
                  {preview.keepsBalance.map((balance) => (
                    <Typography
                      key={balance.containerLabel}
                      component="li"
                      variant="body2"
                      color="text.secondary"
                    >
                      leave {balance.containerLabel} on site with{" "}
                      {formatQuantity(balance.remaining, balance.unit)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )
          )}

          {issues
            .filter((issue) => issue.level !== IssueLevel.Critical)
            .map((issue, i) => (
              <Alert key={`${issue.key}-${i}`} severity="warning">
                {issue.message}
              </Alert>
            ))}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
